/* ============================================================
   NoteModal — modale con textarea nota + conferma, riusabile per
   Rifiuta/Scarta (e affini). Comportamento coerente col DS:
   - chiude con Esc e con click sul backdrop (guardia mousedown)
   - autofocus sulla textarea
   - conferma disabilitata se la nota è obbligatoria e vuota
   Usa le classi condivise .dialog-overlay / .dialog-card / .dialog-*.
   ============================================================ */
import { useEffect, useRef, useState } from "react";

export function NoteModal({
  title,
  label,
  placeholder,
  confirmLabel = "Conferma",
  confirmVariant = "primary",
  requireNote = true,
  warning,
  loading = false,
  onConfirm,
  onClose,
}: {
  title: string;
  label: string;
  placeholder?: string;
  confirmLabel?: string;
  confirmVariant?: "primary" | "danger";
  requireNote?: boolean;
  warning?: React.ReactNode;
  loading?: boolean;
  onConfirm: (note: string) => void;
  onClose: () => void;
}) {
  const [note, setNote] = useState("");
  const downOnBackdrop = useRef(false);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { taRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !loading) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [loading, onClose]);

  const canConfirm = !loading && (!requireNote || note.trim().length > 0);

  return (
    <div
      className="dialog-overlay"
      onMouseDown={(e) => { downOnBackdrop.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (e.target === e.currentTarget && downOnBackdrop.current && !loading) onClose(); }}
    >
      <div className="dialog-card">
        <div className="dialog-header"><h2 style={{ margin: 0 }}>{title}</h2></div>
        {warning}
        <div className="form-group">
          <label className="form-label">{label}</label>
          <textarea
            ref={taRef}
            className="form-textarea"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={placeholder}
            disabled={loading}
          />
        </div>
        <div className="dialog-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={loading}>Annulla</button>
          <button className={`btn btn-${confirmVariant}`} onClick={() => onConfirm(note)} disabled={!canConfirm}>
            {loading ? <span className="spinner" style={{ width: 14, height: 14 }} /> : null}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
